// Feltmelding (PTT) med geolokasjon — «lyser opp i kartet» når noen sender.
//
// Hver melding bærer avsenderens posisjon. Naturlig-språk-parseren tolker
// retning + avstand («200 m nordvest for min posisjon») og projiserer et
// gult markørpunkt i den retningen (samme projeksjon som SPARK modus A). Delt i
// sanntid; mottakere kan lese den opp (TTS) og se markøren tenne på kartet.
import { z } from 'zod';
import { project } from './geo.js';
/** Norske himmelretninger → grader (kompass, 0 = nord, med klokka). */
const DIRECTIONS = [
    { deg: 0, words: ['nord', 'n'] },
    { deg: 45, words: ['nordøst', 'nord-øst', 'nordost', 'nordaust', 'nø', 'ne'] },
    { deg: 90, words: ['øst', 'ost', 'aust', 'ø', 'e'] },
    { deg: 135, words: ['sørøst', 'søraust', 'sydøst', 'sør-øst', 'sø', 'se'] },
    { deg: 180, words: ['sør', 'syd', 's'] },
    { deg: 225, words: ['sørvest', 'sørvest', 'sydvest', 'sør-vest', 'sv', 'sw'] },
    { deg: 270, words: ['vest', 'v', 'w'] },
    { deg: 315, words: ['nordvest', 'nord-vest', 'nordvest', 'nv', 'nw'] },
];
// Lengst først, så «nordvest» matcher før «nord»/«vest».
const DIR_LOOKUP = DIRECTIONS.flatMap((d) => d.words.map((w) => ({ w: w.toLowerCase(), deg: d.deg })))
    .sort((a, b) => b.w.length - a.w.length);
/** Tolker «<avstand> <retning>» (i valgfri rekkefølge) fra fri tekst.
 * Returnerer peiling + avstand i meter, eller null om begge ikke finnes. */
export function parseGeoHint(text) {
    if (!text)
        return null;
    // Slå sammen mellomrom/bindestrek-delte sammensatte retninger («sør øst»,
    // «nord vest») — nb-NO tale-gjenkjenning setter ofte inn mellomrom.
    const t = ` ${text.toLowerCase().replace(/(nord|sør|syd)[\s-]+(øst|aust|vest)/g, '$1$2')} `;
    // Avstand: tall + enhet (m/meter/km/kilometer). Komma eller punktum-desimal.
    const dm = /(\d+(?:[.,]\d+)?)\s*(km|kilometer|m|meter|meters)\b/.exec(t);
    if (!dm)
        return null;
    const val = parseFloat(dm[1].replace(',', '.'));
    if (!Number.isFinite(val) || val <= 0)
        return null;
    const unit = dm[2];
    const rangeM = unit.startsWith('km') || unit === 'kilometer' ? val * 1000 : val;
    if (rangeM > 100_000)
        return null; // urimelig — ikke projiser over 100 km
    // Retning: finn et retnings-ord omgitt av ikke-bokstaver (unngå delmatch).
    let bearingDeg = null;
    for (const { w, deg } of DIR_LOOKUP) {
        const re = new RegExp(`[^a-zæøå]${w}[^a-zæøå]`);
        if (re.test(t)) {
            bearingDeg = deg;
            break;
        }
    }
    if (bearingDeg === null)
        return null;
    return { bearingDeg, rangeM };
}
export const FieldMessageSchema = z.object({
    id: z.string().max(128),
    author: z.string().max(128),
    tsMs: z.number(),
    /** Avsenderens posisjon (lyser opp i kartet). */
    lat: z.number(),
    lon: z.number(),
    text: z.string().max(4000).default(''),
    /** Avledet markørpunkt (fra parseGeoHint + projeksjon), om tolket. */
    markLat: z.number().optional(),
    markLon: z.number().optional(),
});
export function parseFieldMessage(raw) {
    const r = FieldMessageSchema.safeParse(raw);
    return r.success ? r.data : null;
}
/** Bygger en feltmelding fra avsenderposisjon + tekst; projiserer markør hvis
 * teksten inneholder retning + avstand. */
export function makeFieldMessage(params) {
    const hint = parseGeoHint(params.text);
    let markLat;
    let markLon;
    if (hint) {
        const p = project({ lat: params.lat, lon: params.lon }, hint.bearingDeg, hint.rangeM);
        markLat = p.lat;
        markLon = p.lon;
    }
    return { ...params, markLat, markLon };
}
//# sourceMappingURL=field.js.map